import React, { useState, useEffect } from "react";
import { useGetIndividualMovementHistoryQuery, useGetTestIndividualMovementHistoryQuery } from "../../redux/slices/apiSlice";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ColorRing } from "react-loader-spinner";
import CustomTooltip from "./CustomTooltip";

const HigherIndividualMovement = ({accountDetails}) => {
  // Tab configuration with display options and their corresponding API values
  
  const tabOptionsConfig = {
    "Shape": {
      prop_name: "shape",
      options: [
        { display: "Round", value: "0000round" },
        { display: "Princess", value: "0001princess" },
        { display: "Pear", value: "0002pear" },
        { display: "Oval", value: "0003oval" },
        { display: "Emerald", value: "0004emerald" },
        { display: "Marquise", value: "0005marquise" },
        { display: "Cushion", value: "0007cushion_brilliant" },
        { display: "Radiant", value: "0009radiant" },
        { display: "Asscher", value: "0011asscher" }
      ]
    },
    "Carat Range": {
      prop_name: "carat_range",
      options: [
        { display: "5.0-5.99", value: "5.0-5.99" },
        { display: "4.0-4.99", value: "4.0-4.99" },
        { display: "3.0-3.99", value: "3.0-3.99" },
        { display: "2.0-2.99", value: "2.0-2.99" },
        { display: "1.5-1.99", value: "1.5-1.99" },
        { display: "1.0-1.49", value: "1.0-1.49" },
        { display: "0.9-0.99", value: "0.9-0.99" },
        { display: "0.7-0.89", value: "0.7-0.89" },
        { display: "0.5-0.69", value: "0.5-0.69" },
        { display: "0.4-0.49", value: "0.4-0.49" },
        { display: "0.3-0.39", value: "0.3-0.39" },
        { display: "0.2-0.29", value: "0.2-0.29" },
        { display: "0-0.19", value: "0-0.19" },
      ]      
    },

    "Color": {
      prop_name: "color",
      options: [
        { display: "D", value: "0000d" },
        { display: "E", value: "0001e" },
        { display: "F", value: "0002f" },
        { display: "G", value: "0003g" },
        { display: "H", value: "0004h" },
        { display: "I", value: "0005i" },
        { display: "J", value: "0006j" },
      ]
    },

    "Clarity": {
      prop_name: "clarity",
      options: [
        { display: "IF", value: "0001if" },
        { display: "VVS1", value: "0002vvs1" },
        { display: "VVS2", value: "0003vvs2" },
        { display: "VS1", value: "0004vs1" },
        { display: "VS2", value: "0005vs2" },
        { display: "SI1", value: "0006si1" },
        { display: "SI2", value: "0007si2" },
        { display: "I1", value: "0009i1" },
        
      ]
    }, 
  };

  // State for UI
  const [activeTab, setActiveTab] = useState("Shape");
  const [selectedOption, setSelectedOption] = useState(tabOptionsConfig["Shape"].options[0].display);
  
  // State for API parameters
  const [queryParams, setQueryParams] = useState({
    id: accountDetails.account_id, // default seller ID
    prop_name: tabOptionsConfig["Shape"].prop_name,
    prop_value: tabOptionsConfig["Shape"].options[0].value,
    
  });

  // Track the current and previous query params to detect changes
  const [currentQueryId, setCurrentQueryId] = useState("initial");
  const [isChangingParams, setIsChangingParams] = useState(false);

  // Fetch data using current query parameters
  const { data, isLoading, isFetching } = useGetTestIndividualMovementHistoryQuery(queryParams, {
    // This ensures the query will refetch when params change
    refetchOnMountOrArgChange: true
  });

  // Update query parameters when tab or option changes
  useEffect(() => {
    const currentTab = tabOptionsConfig[activeTab];
    const selectedOptionObj = currentTab.options.find(opt => opt.display === selectedOption);
    
    if (selectedOptionObj) {
      // Set the flag that we're changing parameters
      setIsChangingParams(true);
      
      // Generate a unique ID for this query
      const newQueryId = `${currentTab.prop_name}-${selectedOptionObj.value}-${Date.now()}`;
      setCurrentQueryId(newQueryId);
      
      setQueryParams({
        id: accountDetails.account_id,
        prop_name: currentTab.prop_name,
        prop_value: selectedOptionObj.value
      });
    }
  }, [activeTab, selectedOption]);

  // Reset the changing flag when data is loaded
  useEffect(() => {
    if (!isLoading && !isFetching && isChangingParams) {
      setIsChangingParams(false);
    }
  }, [isLoading, isFetching]);

  // When tab changes, also reset the selected option
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedOption(tabOptionsConfig[tab].options[0].display);
  };

  // Process chart data from API response
  const chartData = data?.data
    ? Object.entries(data.data.history_data).map(([week, values]) => ({
        week, // X-axis
        price_change: values.price_change,
        overhang: values.overhang,
        min_ppc: values.min_ppc,
        max_ppc: values.max_ppc,
        price_change_10th: values.price_change_10th,
        price_change_90th: values.price_change_90th,
      }))
    : [];
    
    const [showMin, setShowMin] = useState(false);
    const [showMax, setShowMax] = useState(false)

    const toggleMinLine = () => {
      setShowMin(prevState => {
        const newState = !prevState;
        return newState;
      });
    };
    
    const toggleMaxLine = () => {
      setShowMax(prevState => {
        const newState = !prevState;
        return newState;
      });
      
    }; 

    useEffect(() => {
      // This will run whenever showMin or showMax changes
      // console.log("Min visibility changed:", showMin);
      // console.log("Max visibility changed:", showMax);
      
    }, [showMin, showMax]);  

  // Determine if we should show the loading state
  const showLoader = isLoading || isFetching || isChangingParams;

  return (
    <div className="flex flex-col gap-[0.045rem] mdl:flex-row mdl:mx-auto w-full h-5/6 mb-10">
      {/* Left Panel - Tabs */}
      <div className="w-full mdl:w-max bg-white rounded-l-mdl shadow-mdl pt-4">
        <div className="flex mdl:flex-col gap-2 mdl:w-[145px]">
          {Object.keys(tabOptionsConfig).map((tab) => (
            <div
              key={tab}
              className={`px-4 py-2 cursor-pointer text-left 
              ${
                activeTab === tab
                  ? "text-blue-600 bg-white border border-white mdl:border-r-[6px] mdl:border-b-0 mdl:!border-r-blue-600 mdl:!border-b-transparent border-b-[6px] !border-b-blue-600 font-semibold relative z-10"
                  : "bg-transparent opacity-50 text-gray-700 hover:bg-blue-50 hover:text-blue-500"
              }`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </div>
          ))}
        </div>
      </div>

      {/* Center Panel - Main Display */}
      <div className="w-full mdl:w-full bg-white py-4 border-y mdl:border-y-0 mdl:border-x shadow-mdl border-gray-200">
        <p className="mb-1 flex justify-center gap-1 items-center">
          <span className="font-medium w-[96px] text-end"><strong>{activeTab}</strong></span> 
          <span className="font-medium"><strong>:</strong></span> 
          <span className="font-medium w-[160px] text-start">{selectedOption}</span> 
        </p>

        <div className="w-full h-96">
          {showLoader ? (
            <div className="flex justify-center w-full items-center h-full">
                <ColorRing
                    visible={true}
                    height="60"
                    width="60"
                    ariaLabel="blocks-loading"
                    colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
                />
            </div>
          ) : chartData.length > 0 ? (
          <div className={`w-full h-full py-4 transition-opacity duration-500 ${showLoader ? 'opacity-50' : 'opacity-100'}`}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 30, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 13 }}
                  tickFormatter={(value) => {
                    const [year, month, day] = value.split("-");
                    return `${month}/${day}/${year.slice(2)}`;
                  }}
                />

                <YAxis
                  yAxisId="left"
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 13 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `${value}`}
                  tick={{ fontSize: 13 }}
                />
                <Tooltip
                  formatter={(value) => `${value}`}
                  content={<CustomTooltip />}
                  labelStyle={{ fontWeight: 'bold' }}
                />

                <Line
                  yAxisId="left"
                  type="basis"
                  dataKey="price_change_10th"
                  stroke="#8884d8" // Use transparent color if showMin is false
                  strokeDasharray="4 4"
                  strokeWidth={2.5}
                  dot={false}
                  name="Min"
                  hide={!showMin} // Show the line even if it's transparent
                  isAnimationActive={true} 
                  animationEasing="ease-in-out"
                  animationBegin={2000}
                  animationDuration={5000}
                />
                <Line
                  yAxisId="left"
                  type="basis"
                  dataKey="price_change_90th"
                  stroke="#82ca9d" 
                  strokeDasharray="4 4" 
                  strokeWidth={2.5}
                  dot={false}
                  name="Max"
                  hide={!showMax} // Show the line even if it's transparent
                  isAnimationActive={true} 
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="price_change"
                  stroke="#1f2d3d"
                  strokeWidth={2.5}
                  dot={false}
                  name="Price Change"
                  isAnimationActive={true} 
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="overhang"
                  stroke="#65b0ac"
                  strokeWidth={2.5}
                  dot={false}
                  name="Market Overhang"
                  isAnimationActive={true} 
                />
                <Legend verticalAlign="bottom" height={30} />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-2">
          {/* Min checkbox */}
                <div className="flex items-center gap-1">
                  <label htmlFor="showMinCheckbox" className="flex items-center gap-2 cursor-pointer">
                    <span style={{ 
                      width: "12px", 
                      height: "2px", 
                      backgroundColor: "#8884d8", 
                      display: "inline-block",
                    }}></span>
                    <span>Min</span>
                  </label>
                  <input
                    className="cursor-pointer w-3 h-3 z-99 relative"
                    type="checkbox"
                    id="showMinCheckbox"
                    checked={showMin}
                    onChange={toggleMinLine}
                  />
                </div>
                
                {/* Max checkbox */}
                <div className="flex items-center gap-1">
                  <label htmlFor="showMaxCheckbox" className="flex items-center gap-2 ">
                    <span style={{ 
                      width: "12px", 
                      height: "2px", 
                      backgroundColor: "#82ca9d", 
                      display: "inline-block",
                    }}></span>
                    <span>Max</span>
                  </label>
                  <input
                    className="cursor-pointer w-3 h-3 z-99 relative"
                    type="checkbox"
                    id="showMaxCheckbox"
                    checked={showMax}
                    onChange={toggleMaxLine}
                  />
                </div>
            </div>
        </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p>Market Information not available.</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Radio Buttons */}
      <div className="w-[575px] mdl:w-max py-4 px-6  bg-[#FEFEFF]">
        <div className="flex flex-row  flex-wrap justify-start items-center w-[527px] gap-2 px-2 overflow-y-scroll h-26 
                mdl:flex-col mdl:items-start mdl:justify-between mdl:w-[155px] mdl:h-96">
          {tabOptionsConfig[activeTab].options.map((opt) => (
            <label key={opt.value} className="flex mdl:w-full items-center gap-2">
              <input
                type="radio"
                name="dynamicOptions"
                value={opt.display}
                checked={selectedOption === opt.display}
                onChange={(e) => setSelectedOption(e.target.value)}
              />
              {opt.display}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HigherIndividualMovement;